import React from 'react';
import DemoStage from '../DemoStage.jsx';

/**
 * Animated walkthrough of the admin rule builder: two finished condition rows,
 * a third added with + Condition, and the live match-count preview updating as
 * the row completes. Row anatomy (field / operator / value) and the field,
 * operator and footer-button names mirror RuleBuilder.jsx and the allowlist in
 * utils/ruleFields.js so the demo can never drift from the real builder.
 * Placeholder data only.
 */

const STEPS = [
  {
    caption: 'A rule is a list of conditions over real case fields — field, operator, value.',
    scene: 'two', ms: 2600,
    cursor: 'row1',
  },
  {
    caption: 'The live preview counts the existing cases the rule would match.',
    scene: 'two', ms: 2200,
    cursor: 'preview',
  },
  {
    caption: 'Click + Condition to add another row — rows combine with AND.',
    scene: 'two', ms: 1800,
    cursor: 'add', click: true,
  },
  {
    caption: 'Pick the field and operator, then type or pick a value.',
    scene: 'three', ms: 2400,
    cursor: 'row3',
  },
  {
    caption: 'The match count updates as you finish — 12 cases would carry this tag.',
    scene: 'three-filled', ms: 3200,
    cursor: 'preview',
  },
];

function Box({ children, target, width }) {
  return (
    <span
      className="kb-mock-input"
      data-kb-target={target}
      style={{ fontSize: 12, minHeight: 28, padding: '4px 8px', width, flexShrink: 0 }}
    >
      {children}
    </span>
  );
}

function Row({ first, field, op, value, target, pending }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ width: 30, fontSize: 10, fontWeight: 700, color: 'var(--text-3)', flexShrink: 0 }}>
        {first ? '' : 'AND'}
      </span>
      <Box width={150}>{field}</Box>
      <Box width={112}>{op}</Box>
      <Box width={104} target={target}>
        {pending ? <span className="kb-skel" style={{ width: '70%' }} /> : value}
      </Box>
    </div>
  );
}

export default function RuleBuilderDemo() {
  return (
    <DemoStage steps={STEPS} label="Building an auto-tagging rule" height={230}>
      {(step) => {
        const scene = step?.scene ?? 'two';
        const hasThird = scene === 'three' || scene === 'three-filled';
        const filled = scene === 'three-filled';
        return (
          <div className="kb-mock">
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', marginBottom: 10 }}>
              Match ALL (AND) of the conditions below
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Row first field="Treatment" op="equals" value="LHR" target="row1" />
              <Row field="Severity" op="equals" value="Code 3" />
              {hasThird && (
                <div className="kb-pop">
                  <Row field="Days since reported" op="is more than" value="90" target="row3" pending={!filled} />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button type="button" className="btn ghost sm" tabIndex={-1} data-kb-target="add" style={{ fontSize: 11 }}>
                + Condition
              </button>
              <button type="button" className="btn ghost sm" tabIndex={-1} style={{ fontSize: 11 }}>+ Count condition</button>
              <button type="button" className="btn ghost sm" tabIndex={-1} style={{ fontSize: 11 }}>+ Group</button>
            </div>

            <div data-kb-target="preview" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 10, minHeight: 16 }}>
              {scene === 'three' ? (
                'Finish every condition to preview matching cases.'
              ) : (
                <span key={scene} className="kb-pop" style={{ display: 'inline-block' }}>
                  Matches <strong style={{ color: 'var(--text)' }}>{filled ? 12 : 34}</strong> of 87 cases
                </span>
              )}
            </div>
          </div>
        );
      }}
    </DemoStage>
  );
}
